import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import Banner from "../Banner";
import Card from "../Card";
import GhostButton from "../GhostButton";
import PrimaryButton from "../PrimaryButton";

// SCR-06: the rollback confirmation as a panel under the decision block of SCR-03, not a modal. It takes the focus
// on open; «Ні» or Esc close it and give the focus back to the button that opened it (screens.md §SCR-06, AC-07).
function DecisionConfirm({ newCount, reportCount, failed = false, loading = false, error = false, onConfirm, onClose, returnFocusRef }) {
  const { t } = useTranslation();
  const headingId = useId();
  const heading = useRef(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  useEffect(() => {
    const close = () => {
      onClose();
      returnFocusRef?.current?.focus();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [loading, onClose, returnFocusRef]);

  const dismiss = () => {
    onClose();
    returnFocusRef?.current?.focus();
  };

  return (
    <Card role="region" variant="danger" aria-labelledby={headingId} className="fade-in p-4 flex flex-col gap-3">
      <h2 id={headingId} ref={heading} tabIndex={-1} className="text-base font-medium outline-none">
        {t("syncUnit.rollback.title")}
      </h2>
      <p className="text-xs text-text-muted">
        {!failed && `${t("syncUnit.rollback.newOperations", { count: newCount })} `}
        {t("syncUnit.rollback.reports", { count: reportCount })}
      </p>
      {error && <Banner tone="danger">{t("syncUnit.rollback.error")}</Banner>}
      <div className="flex flex-wrap gap-2">
        <PrimaryButton
          size="lg"
          loading={loading}
          onClick={onConfirm}
          className="!bg-none !bg-danger hover:!brightness-90"
        >
          {t("syncUnit.rollback.confirm")}
        </PrimaryButton>
        <GhostButton size="lg" disabled={loading} onClick={dismiss}>
          {t("syncUnit.rollback.cancel")}
        </GhostButton>
      </div>
    </Card>
  );
}

export default DecisionConfirm;
